import { ErrorMessage, Field, useField } from 'formik';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { IoMdAdd } from 'react-icons/io';

import { emailRegex } from '../utils/constants';
import MultiSelectModalField from './inputs/MultiSelectModalField';
import { RadioField } from './inputs/RadioField';
import { IField } from './interfaces/IField';
import { IMultiSelectField } from './interfaces/IMultiSelectField';

import {
	formatToDateString,
	formatToDateTimeString,
	formatToTimeString,
	parseTimeValue,
} from '@/components/utils/utils';
import { IBaseEntity } from '@/interfaces/api/IBaseEntity';

type Props<T> = IField | IMultiSelectField<T>;

const DefaultField = (props: IField) => (
	<Field
		className={`block w-full h-11 px-4 mt-0 border rounded-md appearance-none focus:outline-none focus:ring-0 focus:border-blue-400 focus:shadow-sm focus:shadow-blue-400 ${
			props.disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'
		}`}
		id={props.name}
		data-test={props.name}
		{...props}
	/>
);

const TextAreaField = (props: IField) => (
	<Field
		as="textarea"
		className={`block w-full p-2.5 ${
			props.disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'
		} border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-0 focus:border-blue-400 focus:shadow-sm focus:shadow-blue-400`}
		id={props.name}
		data-test={props.name}
		{...props}
	/>
);

const DateTimeField = ({ name, disabled, placeholder }: IField) => {
	const [field, , helpers] = useField(name);
	const { setValue } = helpers;
	const value = field.value ? new Date(field.value) : null;

	const handleChange = (datetime: Date | null) => {
		setValue(formatToDateTimeString(datetime));
	};

	return (
		<div className="w-full">
			<DatePicker
				selected={value}
				onChange={handleChange}
				showTimeSelect
				timeFormat="hh:mm aa"
				timeIntervals={5}
				dateFormat="dd/MM/yyyy, hh:mm aa"
				className={`block w-full h-11 px-4 mt-0 border rounded-md appearance-none focus:outline-none focus:ring-0 focus:border-blue-400 focus:shadow-sm focus:shadow-blue-400 ${
					disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'
				}`}
				id={name}
				data-test={name}
				disabled={disabled}
				isClearable
				placeholderText={placeholder || 'Select date and time'}
				wrapperClassName="w-full block"
				autoComplete="off"
			/>
		</div>
	);
};

const DateField = ({ name, disabled, placeholder }: IField) => {
	const [field, , helpers] = useField(name);
	const { setValue } = helpers;
	const value = field.value ? new Date(field.value) : null;

	const handleChange = (date: Date | null) => {
		setValue(formatToDateString(date));
	};

	return (
		<div className="w-full">
			<DatePicker
				selected={value}
				onChange={handleChange}
				dateFormat="dd/MM/yyyy"
				className={`block w-full h-11 px-4 mt-0 border rounded-md appearance-none focus:outline-none focus:ring-0 focus:border-blue-400 focus:shadow-sm focus:shadow-blue-400 ${
					disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'
				}`}
				id={name}
				data-test={name}
				disabled={disabled}
				isClearable
				placeholderText={placeholder || 'Select a date'}
				wrapperClassName="w-full block"
				autoComplete="off"
			/>
		</div>
	);
};

const TimeField = ({ name, disabled, placeholder }: IField) => {
	const [field, , helpers] = useField(name);
	const { setValue } = helpers;

	// Use the new utility function to parse the time value
	const value = parseTimeValue(field.value);

	const handleChange = (time: Date | null) => {
		setValue(formatToTimeString(time));
	};

	return (
		<div className="w-full">
			<DatePicker
				selected={value}
				onChange={handleChange}
				showTimeSelect
				showTimeSelectOnly
				timeIntervals={15}
				timeCaption="Time"
				dateFormat="hh:mm aa"
				timeFormat="hh:mm aa"
				className={`block w-full h-11 px-4 mt-0 border rounded-md appearance-none focus:outline-none focus:ring-0 focus:border-blue-400 focus:shadow-sm focus:shadow-blue-400 ${
					disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'
				}`}
				id={name}
				data-test={name}
				disabled={disabled}
				isClearable
				placeholderText={placeholder || 'Select a time'}
				wrapperClassName="w-full block"
				autoComplete="off"
			/>
		</div>
	);
};

const PasswordField = ({ type, ...rest }: IField) => {
	const [showPassword, setShowPassword] = useState(false);

	return (
		<div className="relative">
			<Field
				type={showPassword ? 'text' : 'password'}
				className={`block w-full p-2.5 ${
					rest.disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'
				} border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-0 focus:border-blue-400 focus:shadow-sm focus:shadow-blue-400`}
				id={rest.name}
				data-test={rest.name}
				{...rest}
			/>
			<button
				type="button"
				className="absolute inset-y-0 right-0 pr-3 flex items-center"
				onClick={() => setShowPassword(!showPassword)}
				tabIndex={-1}
			>
				{showPassword ? (
					<AiOutlineEyeInvisible className="h-5 w-5 text-gray-500" />
				) : (
					<AiOutlineEye className="h-5 w-5 text-gray-500" />
				)}
			</button>
		</div>
	);
};

const SelectField = ({ name, disabled, options }: IField) => {
	const [field] = useField(name);

	return (
		<Field
			as="select"
			className={`block w-full h-11 px-4 mt-0 border rounded-md appearance-none focus:outline-none focus:ring-0 focus:border-blue-400 focus:shadow-sm focus:shadow-blue-400 ${
				disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'
			}`}
			id={name}
			data-test={name}
			disabled={disabled}
			{...field}
		>
			{options?.map((option) => {
				if (typeof option === 'string') {
					return (
						<option key={option} value={option}>
							{option}
						</option>
					);
				} else {
					return (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					);
				}
			})}
		</Field>
	);
};

const MultiEmailField = ({
	name,
	disabled,
}: {
	name: string;
	disabled?: boolean;
}) => {
	const [field, , helpers] = useField<string[]>(name);
	const [inputValue, setInputValue] = useState('');
	const [validationError, setValidationError] = useState<string | null>(null);

	const isValidEmail = (email: string): boolean => {
		return emailRegex.test(email);
	};

	const addEmails = (input: string) => {
		const emailsToAdd: string[] = [];
		const invalidEmails: string[] = [];

		input
			.split(/[\s,]+/)
			.map((e) => e.trim())
			.filter((e) => e.length > 0 && !field.value.includes(e))
			.forEach((email) => {
				if (isValidEmail(email)) {
					emailsToAdd.push(email);
				} else if (email.length > 0) {
					invalidEmails.push(email);
				}
			});

		if (invalidEmails.length > 0) {
			setValidationError(`Invalid email format: ${invalidEmails.join(', ')}`);
			setTimeout(() => setValidationError(null), 3000);
		}

		if (emailsToAdd.length > 0) {
			helpers.setValue([...field.value, ...emailsToAdd]);
			setInputValue('');
			setValidationError(null);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			addEmails(inputValue);
		}
	};

	const removeEmail = (emailToRemove: string) => {
		helpers.setValue(field.value.filter((e) => e !== emailToRemove));
	};

	return (
		<div>
			<div className="relative">
				<input
					type="text"
					disabled={disabled}
					placeholder="Enter emails to be invited"
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					className={`w-full p-2 border rounded text-sm ${
						disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-50'
					}`}
				/>
				<button
					type="button"
					onClick={() => addEmails(inputValue)}
					className="absolute inset-y-0 right-0 pr-2 flex items-center"
					disabled={!inputValue.trim()}
				>
					<IoMdAdd className="h-5 w-5 text-blue-500 hover:text-blue-700" />
				</button>
			</div>

			{validationError && (
				<div className="text-red-500 text-xs mt-1">{validationError}</div>
			)}

			<div className="mt-2 flex flex-wrap gap-2">
				{field.value.map((email) => (
					<div
						key={email}
						className="flex items-center bg-blue-100 rounded px-2 py-1 text-sm"
					>
						<span>{email}</span>
						{!disabled && (
							<button
								type="button"
								onClick={() => removeEmail(email)}
								className="ml-1 text-red-500 font-bold"
							>
								&times;
							</button>
						)}
					</div>
				))}
			</div>
		</div>
	);
};
const FormField = <T extends IBaseEntity>(props: Props<T>) => {
	const renderField = () => {
		switch (props.type) {
			case 'textarea':
				return <TextAreaField {...props} />;
			case 'select':
				return <SelectField {...props} />;
			case 'radio':
				return <RadioField {...props} />;
			case 'multiSelect':
				return <MultiSelectModalField {...props} />;
			case 'timepicker':
				return <TimeField {...props} />;
			case 'datepicker':
				return <DateField {...props} />;
			case 'datetimepicker':
				return <DateTimeField {...props} />;
			case 'password':
				return <PasswordField {...props} />;
			case 'emailMultiInput':
				return <MultiEmailField {...props} />;
			default:
				return <DefaultField {...props} />;
		}
	};

	return (
		<div className="w-full pb-4">
			<label
				htmlFor={props.name}
				className="block mb-1 text-sm font-medium text-gray-900"
			>
				{props.label}
			</label>

			{renderField()}

			<div
				className="w-full h-fit py-1"
				data-test={`form-input-error-${props.name}`}
			>
				<ErrorMessage
					data-cy="form-field-error"
					data-test={`form-input-error-${props.name}`}
					className="text-red-500 text-xs w-full"
					name={props.name}
					component="p"
				/>
			</div>
		</div>
	);
};

export default FormField;
